import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiEdit3, FiPlus, FiTrash2, FiX, FiCheck, FiChevronRight, FiChevronLeft } = FiIcons;

const NotesManager = ({ entityId, entityType }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // Storage key based on entity (product or customer)
  const storageKey = `notes_${entityType}_${entityId}`;

  useEffect(() => {
    const savedNotes = localStorage.getItem(storageKey);
    if (savedNotes) {
      setNotes(JSON.parse(savedNotes));
    }
  }, [storageKey]);

  const saveNotes = (updatedNotes) => {
    setNotes(updatedNotes);
    localStorage.setItem(storageKey, JSON.stringify(updatedNotes));
  };

  const addNote = () => {
    if (!newNote.trim()) return;
    const note = {
      id: Date.now(),
      text: newNote,
      date: new Date().toLocaleString(),
    };
    const updatedNotes = [note, ...notes];
    saveNotes(updatedNotes);
    setNewNote('');
    setIsAdding(false);
  };

  const deleteNote = (id) => {
    const updatedNotes = notes.filter(n => n.id !== id);
    saveNotes(updatedNotes);
  };

  return (
    <div className="relative">
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors text-gray-700 font-medium"
      >
        <SafeIcon icon={isOpen ? FiX : FiEdit3} className="text-blue-600" />
        <span>{isOpen ? 'Close Notes' : 'Notes'}</span>
        {notes.length > 0 && !isOpen && (
          <span className="bg-blue-600 text-white text-[10px] w-5 h-5 flex items-center justify-center rounded-full">
            {notes.length}
          </span>
        )}
      </button>

      {/* Expanded Notes Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop for mobile or focus */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 z-40 bg-black/5 lg:hidden"
            />
            
            <motion.div
              initial={{ opacity: 0, x: 20, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 20, scale: 0.95 }}
              className="absolute right-0 mt-2 w-80 md:w-96 bg-white rounded-2xl shadow-xl border border-gray-200 z-50 overflow-hidden"
            >
              {/* Header */}
              <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <SafeIcon icon={FiEdit3} className="text-blue-600" />
                  Quick Notes
                </h3>
                <button
                  onClick={() => setIsAdding(!isAdding)}
                  className="p-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  title="Add Note"
                >
                  <SafeIcon icon={isAdding ? FiX : FiPlus} />
                </button>
              </div>

              {/* Add Note Input */}
              <AnimatePresence>
                {isAdding && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="p-4 border-b border-gray-100 bg-blue-50/30"
                  >
                    <textarea
                      autoFocus
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Type your note here..."
                      className="w-full p-3 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none h-24"
                    />
                    <div className="flex justify-end gap-2 mt-2">
                      <button
                        onClick={() => setIsAdding(false)}
                        className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={addNote}
                        className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 flex items-center gap-1"
                      >
                        <SafeIcon icon={FiCheck} /> Save Note
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Notes List */}
              <div className="max-h-[400px] overflow-y-auto p-4 space-y-3">
                {notes.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <SafeIcon icon={FiEdit3} className="text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No notes yet. Start by adding one!</p>
                  </div>
                ) : (
                  notes.map((note) => (
                    <motion.div
                      key={note.id}
                      layout
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="group p-3 bg-white border border-gray-100 rounded-xl hover:border-blue-200 hover:shadow-sm transition-all"
                    >
                      <div className="flex justify-between items-start gap-2">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap flex-1">{note.text}</p>
                        <button
                          onClick={() => deleteNote(note.id)}
                          className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <SafeIcon icon={FiTrash2} className="text-xs" />
                        </button>
                      </div>
                      <div className="mt-2 text-[10px] text-gray-400 font-medium italic">
                        {note.date}
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotesManager;